import { useCallback, useEffect, useMemo, useState } from 'react'

import { Plus } from '@gravity-ui/icons'
import type { SortDescriptor } from '@heroui/react'
import { Button, Spinner } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '@/components/ui/PageHeader'
import { usePaymentsStore } from '@/store/payments.store'
import { SortDirection } from '@/utils/sorting'

import PaymentDialog from './components/PaymentDialog'
import { SettlementsFilters } from './components/SettlementsFilters'
import { SettlementsTable } from './components/SettlementsTable'

export default function SettlementsList() {
  const { t } = useTranslation()
  const {
    isLoading,
    pagination,
    setPagination,
    filters,
    setFilters,
    fetchSettlements,
  } = usePaymentsStore()

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'name',
    direction: 'ascending',
  })
  const [isPaymentOpen, setIsPaymentOpen] = useState(false)

  useEffect(() => {
    setPagination({
      sort: {
        key: sortDescriptor.column as string,
        direction: sortDescriptor.direction === 'ascending' ? SortDirection.ASC : SortDirection.DESC,
      },
    })
  }, [setPagination, sortDescriptor])

  const handleDriverChange = useCallback((driverID: string) => {
    setFilters({ driverID })
  }, [setFilters])

  const handlePhoneChange = useCallback((phone: string) => {
    setFilters({ phone })
  }, [setFilters])

  const handleOpenPayment = useCallback(() => {
    setIsPaymentOpen(true)
  }, [])

  const handlePaymentSuccess = useCallback(() => {
    fetchSettlements()
  }, [fetchSettlements])

  const headerActions = useMemo(() => (
    <Button onClick={handleOpenPayment}>
      <Plus />
      {t('settlements.add_payment')}
    </Button>
  ), [handleOpenPayment, t])

  return (
    <div className="p-5 space-y-5">
      <PageHeader
        title={t('settlements.title')}
        subtitle={`${pagination.total} ${t('settlements.subtitle')}`}
        actions={headerActions}
      />

      <SettlementsFilters
        driverID={filters.driverID}
        phone={filters.phone}
        onDriverChange={handleDriverChange}
        onPhoneChange={handlePhoneChange}
      />

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <SettlementsTable
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        />
      )}

      <PaymentDialog
        isOpen={isPaymentOpen}
        onOpenChange={setIsPaymentOpen}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  )
}
